/**
 * The progress dialog `pages/orders/Complete.tsx` shows while a run takes an order's steps.
 *
 * vendor/isomorphic/apps/isomorphic/src/app/shared/modal-views/container.tsx
 *   the rizzui `Modal` and its dark-mode overlay;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/financial/dashboard/investment/investment.tsx
 *   `motion` from `motion/react` for the animation.
 *
 * Nothing here decides anything: which step is running, done or stopped is the page's `statusOf`,
 * read from the order and the run. The ring counts steps — how many of the plan are done — never money.
 */
import { useEffect, useState, type ReactNode } from "react";
import { AnimatePresence, MotionConfig, motion } from "motion/react";
import {
  PiArrowRightBold as ArrowRight,
  PiCheckBold as Check,
  PiHandGrabbingDuotone as PickIcon,
  PiLockSimpleDuotone as LockIcon,
  PiPackageDuotone as PackIcon,
  PiReceiptDuotone as InvoiceIcon,
  PiSealCheckDuotone as ApproveIcon,
  PiSignatureDuotone as DeliverIcon,
  PiTimerDuotone as TimerIcon,
  PiTruckDuotone as TruckIcon,
  PiWarningBold as Warning,
} from "react-icons/pi";
import { Button, Modal, Text, Title } from "rizzui";
import { cn } from "@steelyard/theme";
import { stateLabel } from "../../lib/services/ordering";

export type StepStatus = "todo" | "running" | "stopped" | "done";
export type ProgressPhase = "running" | "invoiced" | "stopped";

const ICON: Record<string, (props: { className?: string }) => ReactNode> = {
  approved: (p) => <ApproveIcon aria-hidden {...p} />,
  picking_packing: (p) => <PickIcon aria-hidden {...p} />,
  packed: (p) => <PackIcon aria-hidden {...p} />,
  invoiced: (p) => <InvoiceIcon aria-hidden {...p} />,
  dispatched: (p) => <TruckIcon aria-hidden {...p} />,
  delivered: (p) => <DeliverIcon aria-hidden {...p} />,
};

const stepIcon = (step: string, className: string) => (ICON[step] ?? ICON.packed)({ className });

const RING = 34;
const CIRCUMFERENCE = 2 * Math.PI * RING;

export function CompleteProgress({
  open,
  phase,
  orderNumber,
  from,
  to,
  plan,
  statusOf,
  running,
  message,
  invoiceNumber,
  continueLoading,
  onContinue,
  onClose,
}: {
  open: boolean;
  phase: ProgressPhase;
  orderNumber: string;
  from: string;
  to: string;
  plan: string[];
  statusOf: (step: string) => StepStatus;
  running: string | null;
  /** What is happening now, in words. */
  message: string;
  invoiceNumber: string | null;
  continueLoading: boolean;
  onContinue: () => void;
  onClose: () => void;
}) {
  const done = plan.filter((step) => statusOf(step) === "done").length;
  const share = plan.length > 0 ? done / plan.length : 0;
  const current = running ?? plan.find((step) => statusOf(step) !== "done") ?? to;

  // Seconds since the run started, while it runs — the invoice wait is the long part.
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    if (open) setSeconds(0);
  }, [open]);
  useEffect(() => {
    if (!open || phase !== "running") return;
    const id = window.setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => window.clearInterval(id);
  }, [open, phase]);
  const elapsed = `${String(Math.trunc(seconds / 60))}:${String(seconds % 60).padStart(2, "0")}`;

  const band =
    phase === "stopped"
      ? "from-orange-dark via-orange-dark to-[#7c2d12]"
      : phase === "invoiced"
        ? "from-green-dark via-green-dark to-[#14532d]"
        : "from-primary via-primary-dark to-[#0b1f4d]";

  return (
    <Modal
      isOpen={open}
      onClose={() => phase !== "running" && onClose()}
      size="md"
      overlayClassName="backdrop-blur-sm dark:bg-opacity-40 dark:backdrop-blur-lg"
      containerClassName="overflow-hidden !rounded-3xl !p-0 shadow-2xl dark:bg-gray-100"
    >
      <MotionConfig reducedMotion="user">
        <div role="status" aria-live="polite" aria-label={`${orderNumber}: ${message}`}>
          {/* ── Header band: ring, title, timer ───────────────────────────────────────────── */}
          <div className={cn("relative overflow-hidden bg-gradient-to-br px-6 pb-6 pt-7 text-white transition-colors duration-700", band)}>
            <motion.span
              aria-hidden
              className="pointer-events-none absolute -end-16 -top-20 size-56 rounded-full bg-white/15 blur-3xl"
              animate={{ scale: [1, 1.2, 1], opacity: [0.4, 0.75, 0.4] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.span
              aria-hidden
              className="pointer-events-none absolute -bottom-24 -start-10 size-48 rounded-full bg-white/10 blur-3xl"
              animate={{ x: [0, 24, 0], opacity: [0.3, 0.6, 0.3] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            />

            <div className="relative flex items-center gap-5">
              <div className="relative grid size-20 shrink-0 place-items-center">
                <svg viewBox="0 0 80 80" className="absolute inset-0 -rotate-90" aria-hidden>
                  <circle cx="40" cy="40" r={RING} className="fill-none stroke-white/20" strokeWidth="5" />
                  <motion.circle
                    cx="40"
                    cy="40"
                    r={RING}
                    className="fill-none stroke-white"
                    strokeWidth="5"
                    strokeLinecap="round"
                    strokeDasharray={CIRCUMFERENCE}
                    initial={{ strokeDashoffset: CIRCUMFERENCE }}
                    animate={{ strokeDashoffset: CIRCUMFERENCE * (1 - share) }}
                    transition={{ duration: 0.9, ease: "easeOut" }}
                  />
                </svg>
                <AnimatePresence mode="wait">
                  <motion.span
                    key={phase === "running" ? current : phase}
                    initial={{ scale: 0.4, opacity: 0, rotate: -20 }}
                    animate={{ scale: 1, opacity: 1, rotate: 0 }}
                    exit={{ scale: 0.4, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 260, damping: 18 }}
                    className="grid size-12 place-items-center rounded-full bg-white/15 ring-1 ring-white/30 backdrop-blur"
                  >
                    {phase === "invoiced" ? (
                      <svg viewBox="0 0 24 24" className="size-7" aria-hidden>
                        <motion.path
                          d="M5 12.5l4.5 4.5L19 7.5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2.6}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          initial={{ pathLength: 0 }}
                          animate={{ pathLength: 1 }}
                          transition={{ duration: 0.55, delay: 0.15, ease: "easeOut" }}
                        />
                      </svg>
                    ) : phase === "stopped" ? (
                      <Warning className="size-6" aria-hidden />
                    ) : (
                      stepIcon(current, "size-6")
                    )}
                  </motion.span>
                </AnimatePresence>
              </div>

              <div className="min-w-0 flex-1">
                <Text className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/70">
                  {phase === "invoiced" ? "Invoice finalised" : phase === "stopped" ? "Waiting on Billing" : "Completing steps"}
                </Text>
                <Title as="h3" className="mt-1 truncate font-lexend text-xl font-semibold text-white">
                  {orderNumber}
                </Title>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-sm text-white/80">
                  {stateLabel(from)}
                  <ArrowRight aria-hidden className="size-3" />
                  <span className="font-medium text-white">{stateLabel(to)}</span>
                </div>
              </div>

              <div className="flex shrink-0 flex-col items-end gap-1.5 self-start">
                <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 text-xs font-medium tabular-nums ring-1 ring-white/20">
                  <TimerIcon aria-hidden className="size-3.5" />
                  {elapsed}
                </span>
                <span className="text-xs tabular-nums text-white/70">
                  {done} of {plan.length} done
                </span>
              </div>
            </div>
          </div>

          {/* ── What is happening now ─────────────────────────────────────────────────────── */}
          <div className="border-b border-muted bg-gray-50/60 px-6 py-3.5 dark:bg-gray-50">
            <AnimatePresence mode="wait">
              <motion.p
                key={message}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.25 }}
                className="text-sm leading-relaxed text-gray-700"
              >
                {message}
              </motion.p>
            </AnimatePresence>
          </div>

          {/* ── The steps ─────────────────────────────────────────────────────────────────── */}
          <ol className="flex flex-col px-6 py-5">
            {plan.map((step, index) => {
              const status = statusOf(step);
              const last = index === plan.length - 1;
              return (
                <motion.li
                  key={step}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * index, duration: 0.3 }}
                  className="relative flex gap-4 pb-5 last:pb-0"
                >
                  {last ? null : (
                    <span aria-hidden className="absolute start-[19px] top-11 h-[calc(100%-2.75rem)] w-0.5 overflow-hidden rounded-full bg-gray-200">
                      <motion.span
                        className="block w-full rounded-full bg-green"
                        initial={false}
                        animate={{ height: status === "done" ? "100%" : "0%" }}
                        transition={{ duration: 0.6, ease: "easeInOut" }}
                      />
                    </span>
                  )}

                  <span className="relative grid size-10 shrink-0 place-items-center">
                    {status === "running" ? (
                      <>
                        <span aria-hidden className="absolute inset-0 animate-ping rounded-full bg-primary/20 [animation-duration:1.8s]" />
                        <span aria-hidden className="absolute -inset-1 animate-spin rounded-full border-2 border-primary/15 border-t-primary [animation-duration:1.1s]" />
                      </>
                    ) : null}
                    <span
                      className={cn(
                        "relative grid size-10 place-items-center rounded-full transition-colors duration-500",
                        status === "done" && "bg-green text-white shadow-md shadow-green/30",
                        status === "running" && "bg-primary text-white shadow-lg shadow-primary/30",
                        status === "stopped" && "bg-orange text-white shadow-md shadow-orange/30",
                        status === "todo" && "bg-gray-100 text-gray-400",
                      )}
                    >
                      <AnimatePresence mode="wait" initial={false}>
                        <motion.span
                          key={status}
                          initial={{ scale: 0.3, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0.3, opacity: 0 }}
                          transition={{ type: "spring", stiffness: 420, damping: 20 }}
                          className="grid place-items-center"
                        >
                          {status === "done" ? (
                            <Check className="size-4" aria-hidden />
                          ) : status === "stopped" ? (
                            <Warning className="size-4" aria-hidden />
                          ) : (
                            stepIcon(step, "size-5")
                          )}
                        </motion.span>
                      </AnimatePresence>
                    </span>
                  </span>

                  <div className="min-w-0 flex-1 pt-1">
                    <div className="flex items-center justify-between gap-3">
                      <Text className={cn("font-medium transition-colors", status === "todo" ? "text-gray-500" : "text-gray-900")}>
                        {stateLabel(step)}
                      </Text>
                      <Text
                        className={cn(
                          "shrink-0 text-xs font-medium",
                          status === "done" && "text-green-dark",
                          status === "running" && "text-primary",
                          status === "stopped" && "text-orange-dark",
                          status === "todo" && "text-gray-400",
                        )}
                      >
                        {status === "done" ? "Done" : status === "running" ? "In progress" : status === "stopped" ? "Waiting" : "Up next"}
                      </Text>
                    </div>
                    {status === "running" ? (
                      <div className="relative mt-2.5 h-1 overflow-hidden rounded-full bg-primary/10">
                        <motion.span
                          aria-hidden
                          className="absolute inset-y-0 w-1/3 rounded-full bg-gradient-to-r from-transparent via-primary to-transparent"
                          animate={{ x: ["-120%", "320%"] }}
                          transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
                        />
                      </div>
                    ) : null}
                  </div>
                </motion.li>
              );
            })}
          </ol>

          {/* ── Footer ────────────────────────────────────────────────────────────────────── */}
          <div className="border-t border-muted px-6 py-4">
            {phase === "invoiced" ? (
              <div className="flex flex-col gap-3">
                {invoiceNumber ? (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="flex items-center justify-between rounded-xl bg-green-lighter/60 px-4 py-2.5 text-sm"
                  >
                    <span className="flex items-center gap-2 text-green-dark">
                      <InvoiceIcon aria-hidden className="size-4" /> Invoice
                    </span>
                    <span className="font-mono font-semibold text-gray-900">{invoiceNumber}</span>
                  </motion.div>
                ) : null}
                <Button type="button" size="lg" className="group w-full" isLoading={continueLoading} onClick={onContinue}>
                  Continue to dispatch
                  <ArrowRight aria-hidden className="ms-2 size-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Button>
              </div>
            ) : phase === "stopped" ? (
              <Button type="button" variant="outline" size="lg" className="w-full" onClick={onClose}>
                Close
              </Button>
            ) : (
              <Text className="flex items-center gap-2 text-xs text-gray-500">
                <LockIcon aria-hidden className="size-4 shrink-0 text-gray-400" />
                Keep this page open until it finishes — each step is saved as it completes.
              </Text>
            )}
          </div>
        </div>
      </MotionConfig>
    </Modal>
  );
}
